import BaseAuthProvider from './BaseAuthProvider'
import AuthService from '../service'
import { AuthProviders } from '../constants/authProviders'

/**
 * Magic Link Authentication Provider (Passwordless)
 * Two-step authentication via email link:
 * 1. User enters email → receives magic link via email with temporary code (8 digits)
 * 2. User clicks link → frontend auto-verifies code → authenticated
 *
 * Unlike email_token which requires manual code entry, magic_link embeds the code
 * in the URL for automatic verification. The code is verified server-side and
 * exchanged for a proper auth token.
 *
 * URL format: /login?magic_link=47382916
 */
export class MagicLinkAuthProvider extends BaseAuthProvider {
    getMethod() {
        return AuthProviders.MAGIC_LINK
    }

    /**
     * Magic link provider is enabled when:
     * 1. Listed in .env providers (for manual email entry flow)
     * 2. OR magic_link param is present in URL (for auto-verification)
     */
    isEnabled(context) {
        const inEnv = context.envProviders.includes(this.getMethod())
        const hasMagicLink = !!(context.queryParams && context.queryParams.magic_link)
        return inEnv || hasMagicLink
    }

    /**
     * Check if we have magic link params in URL for auto-verification
     */
    static hasAutoVerifyParams(queryParams) {
        return !!(queryParams && queryParams.magic_link && queryParams.email)
    }

    /**
     * Request magic link - check if user exists and send magic link
     * @param {string} email - User email address
     * @returns {Promise<{status: string, email: string}>}
     */
    async requestMagicLink(email) {
        const response = await AuthService.requestMagicLink(email)
        return response.data
    }

    /**
     * Register new user and send magic link
     * Used for new users in the magic link flow
     * @param {Object} data - Registration data
     * @returns {Promise<{status: string, email: string}>}
     */
    async registerAndSendMagicLink(data) {
        const response = await AuthService.registerUserMagicLink(data)
        return response.data
    }

    /**
     * Verify magic link code and authenticate
     * Called when user lands on page with ?magic_link=CODE&email=xxx
     *
     * @param {Object} credentials
     * @param {string} credentials.email - User email from URL
     * @param {string} credentials.code - Magic link code from URL (8 digits)
     * @returns {Promise<{token: string, tokenType: string}>}
     */
    async authenticate({ email, code }) {
        const response = await AuthService.verifyMagicLink(email, code)

        return {
            token: response.data.token,
            tokenType: 'Token',
            ...response.data
        }
    }

    /**
     * Suggest alternatives if user wants different login method
     */
    getAlternatives(enabledProviders) {
        const alternatives = []

        if (enabledProviders.includes(AuthProviders.EMAIL_TOKEN)) {
            alternatives.push(AuthProviders.EMAIL_TOKEN)
        }

        if (enabledProviders.includes(AuthProviders.PASSWORD)) {
            alternatives.push(AuthProviders.PASSWORD)
        }

        return alternatives
    }
}

export default MagicLinkAuthProvider
